import {attr, controller, target} from '@github/catalyst'

/**
 * This code is maintained in `github/media-players` which is also a playground for experimenting.
 * Please do your best so the uptades made are in sync.
 **/
@controller
export default class AudioPlayer extends HTMLElement {
  requestedAnimationFrame = 0
  volumeMemo = 1

  browserIsIOS: boolean =
    /iPad|iPhone|iPod/gi.test(navigator.userAgent) && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1
  browserIsMobile: boolean = this.isMobile()

  @attr isReady = false
  @attr isPlaying = false
  @attr defaultSkipStep = 10
  @attr defaultVolumeStep = 0.1
  @attr volumeState = 'full'
  @attr declare showTooltips: boolean
  @attr isBuffering = true
  @attr isLive = false

  @target declare media: HTMLMediaElement
  @target declare playToggleButton: HTMLElement
  @target declare playTooltip: HTMLElement
  @target declare scrubber: HTMLInputElement
  @target declare scrubberProgress: HTMLProgressElement
  @target declare scrubberTooltip: HTMLElement
  @target declare currentTimeLabel: HTMLElement
  @target declare diffTimeLabel: HTMLElement
  @target declare totalTimeLabel: HTMLElement
  @target declare muteToggleButton: HTMLElement
  @target declare muteToggleTooltip: HTMLElement
  @target declare volumeSlider: HTMLInputElement
  @target declare volumeProgress: HTMLProgressElement
  @target declare bufferProgress: HTMLProgressElement

  connectedCallback() {
    if (!this.media) return
    if (!this.isConnected) return
    if (this.isReady) return

    if (this.browserIsMobile) this.classList.add('media-player-browser-is-mobile')

    this.dispatchEvent(new CustomEvent('media-player-connected'))

    this.media.addEventListener('ended', () => {
      this.ended()
    })

    this.media.addEventListener('play', () => {
      this.isBuffering = false
      this.interactionEvent('play')
      this.setPlayState(false)
    })

    this.media.addEventListener('pause', () => {
      this.isBuffering = false
      this.interactionEvent('pause')
      this.setPlayState(true)
    })

    // BUFFERING STATES
    this.media.addEventListener('waiting', () => {
      this.isBuffering = true
    })

    this.media.addEventListener('playing', () => {
      this.isBuffering = false
    })

    // `durationchange`, `loadedmetadata`, `loadeddata`, `canplaythrough` and `canplay` are used differently accross the browsers, for that reason we have all
    this.events(this.media, 'durationchange loadedmetadata loadeddata canplay canplaythrough', () => {
      if (this.media.duration && !isNaN(this.media.duration)) this.loaded()
    })

    // In case the native players have already triggered the events due the small media files this will make sure that the player is initialized
    // We know that metadata has been loaded if the player knows the duration of the media
    if (this.media && this.media.duration && !isNaN(this.media.duration)) {
      this.loaded()
    }

    this.addEventListener('mousedown', () => {
      this.interactionEvent('click')
    })

    this.addEventListener('focus', () => {
      this.interactionEvent('focus')
    })
  }

  loaded() {
    if (this.isReady || !this.media) return
    this.isReady = true
    this.isBuffering = false
    this.isLive = this.media.duration >= 2 ** 32 // 2^32 duration is lowest number for live streaming duration. Learned from: https://github.com/sampotts/plyr/blob/20bf5a883306e9303b325e72c9102d76cc733c47/src/js/controls.js#L780
    this.setTotalTimeLabel(this.media.duration)
    this.setDiffTimeLabel(0, this.media.duration)
    this.initScrubber()
    this.restoreVolumeState()
    this.initVolumeSlider()
    this.initBufferingProgress()

    for (const element of this.querySelectorAll('[disabled]')) {
      element.removeAttribute('disabled')
    }

    this.dispatchEvent(new CustomEvent('media-player-ready'))
  }

  ended() {
    this.pause()
  }

  // PLAY, PAUSE, STOP
  play() {
    this.media?.play()
  }

  pause() {
    this.media?.pause()
  }

  stop() {
    this.pause()
    if (this.media) this.media.currentTime = 0
    this.setScrubberPosition(0)
  }

  togglePlay() {
    if (this.media?.paused) {
      this.play()
      return
    }

    this.pause()
  }

  setPlayState(isPaused: boolean) {
    this.isPlaying = !isPaused

    if (isPaused) {
      cancelAnimationFrame(this.requestedAnimationFrame)
    } else {
      requestAnimationFrame(this.updateStateWhilePlaying.bind(this))
    }

    if (!this.playToggleButton) return

    const label = isPaused
      ? this.playToggleButton.getAttribute('data-label-play')
      : this.playToggleButton.getAttribute('data-label-pause')
    if (label) {
      this.playToggleButton.setAttribute('aria-label', label)
      if (this.playTooltip && this.showTooltips) this.playTooltip.textContent = label
    }
  }

  // SCRUBBER
  setTotalTimeLabel(value: number) {
    if (!this.totalTimeLabel || this.isLive) return
    this.totalTimeLabel.textContent = this.formatTime(value)
  }

  setCurrentTimeLabel(value: number) {
    if (!this.currentTimeLabel || this.isLive) return
    this.currentTimeLabel.textContent = this.formatTime(value)
  }

  setDiffTimeLabel(current: number, total: number) {
    if (!this.diffTimeLabel || this.isLive) return
    const diffTime = -(current - total)

    if (diffTime < 1) {
      this.diffTimeLabel.textContent = ` ${this.formatTime(0)}`
      return
    }

    this.diffTimeLabel.textContent = `-${this.formatTime(diffTime)}`
  }

  updateScrubberAria() {
    if (!this.scrubber) return
    this.scrubber.setAttribute('aria-valuenow', this.scrubber.value)
  }

  updateScrubberTooltip(x: number) {
    if (!this.showTooltips || !this.scrubberTooltip) return
    const maxValue = parseInt(this.scrubber.max, 10)
    const percent = this.calculatePercentagePostion(x, this.scrubber.offsetWidth)
    let time = (maxValue * percent) / 100

    if (time < 0) time = 0
    if (time > maxValue) time = maxValue

    this.scrubberTooltip.style.left = `${percent}%`
    if (this.scrubberTooltip.firstChild) this.scrubberTooltip.firstChild.textContent = this.formatTime(time)
  }

  updateStateWhilePlaying() {
    if (!this.media || this.isLive) return
    this.setCurrentTimeLabel(this.media.currentTime)
    this.setDiffTimeLabel(this.media.currentTime, this.media.duration)
    this.setScrubberPosition(this.media.currentTime)

    this.requestedAnimationFrame = requestAnimationFrame(this.updateStateWhilePlaying.bind(this))
  }

  setScrubberPosition(value: number) {
    if (!this.scrubber || this.isLive) return
    const flooredValue = Math.floor(value)
    this.scrubber.value = `${flooredValue}`
    if (this.scrubberProgress) this.scrubberProgress.value = flooredValue
    this.updateScrubberAria()
  }

  initScrubber() {
    if (!this.scrubber || !this.media || this.isLive) return
    const max = Math.floor(this.media.duration)
    this.scrubber.value = `0` // reset scrubber value due Moz FF not reseting it on load for an unknown reason
    this.scrubber.min = `0`
    this.scrubber.max = `${max}`
    this.scrubber.setAttribute('aria-valuemin', this.scrubber.min)
    this.scrubber.setAttribute('aria-valuemax', this.scrubber.max)

    if (this.scrubberProgress) this.scrubberProgress.max = max

    this.scrubber.addEventListener('change', () => {
      if (!this.media) return
      this.media.currentTime = parseInt(this.scrubber.value, 10)

      if (!this.media.paused) {
        requestAnimationFrame(this.updateStateWhilePlaying.bind(this))
      }
    })

    this.scrubber.addEventListener('input', () => {
      if (!this.media) return
      const value = parseInt(this.scrubber.value, 10)
      this.setCurrentTimeLabel(value)
      this.setDiffTimeLabel(value, this.media.duration)
      if (this.scrubberProgress) this.scrubberProgress.value = value
      this.updateScrubberAria()

      if (!this.media.paused) {
        cancelAnimationFrame(this.requestedAnimationFrame)
      }
    })

    this.scrubber.addEventListener('mousemove', event => {
      this.updateScrubberTooltip(event.offsetX)
    })
  }

  // VOLUME
  updateVolumeSlider() {
    if (!this.media) return
    const value = this.media.volume * 100
    this.setVolumeSlider(value)
  }

  setVolumeSlider(value: number) {
    if (!this.volumeSlider) return
    this.volumeSlider.setAttribute('aria-valuenow', `${value}`)
    this.volumeSlider.value = `${value}`
    if (this.volumeProgress) this.volumeProgress.value = value
  }

  updateMuteButtonState() {
    if (!this.media) return
    const value = this.media.volume
    this.setMuteButtonState(value)
  }

  setMuteButtonState(value: number) {
    if (!this.muteToggleButton) return
    const label =
      value > 0
        ? this.muteToggleButton.getAttribute('data-label-unmute')
        : this.muteToggleButton.getAttribute('data-label-mute')

    if (label) {
      this.muteToggleButton.setAttribute('aria-label', label)
      if (this.showTooltips && this.muteToggleTooltip) this.muteToggleTooltip.textContent = label
    }

    if (value < 0.1) {
      this.volumeState = 'mute'
      return
    }

    if (value < 0.6) {
      this.volumeState = 'mid'
      return
    }

    this.volumeState = 'full'
  }

  updateVolumeState() {
    this.updateVolumeSlider()
    this.updateMuteButtonState()
  }

  toggleMute() {
    if (!this.media) return
    this.setMute(!this.media.muted)
  }

  setMute(mute: boolean, triggerInteractionEvent = true, storeValue = true) {
    if (!this.media || !this.muteToggleButton) return // without muteToggleButton element present you shouldn't be able to set mute
    this.media.muted = mute

    if (!mute) {
      let volumeValue = this.volumeMemo || this.defaultVolumeStep
      if (!this.volumeSlider) volumeValue = 1
      this.media.volume = volumeValue
    } else {
      this.media.volume = 0
    }

    if (triggerInteractionEvent) this.interactionEvent(mute ? 'mute' : 'unmute')
    if (storeValue) this.storeValue('muted', mute)
  }

  restoreVolumeState() {
    if (!this.media) return
    const volume = this.retrieveValue('volume') as number
    const muted = this.retrieveValue('muted') as boolean

    if (this.volumeSlider && volume !== null) this.setVolume(volume, false, false)
    if (this.muteToggleButton && muted) this.setMute(muted, false, false)
    this.updateVolumeState()
    this.media.addEventListener('volumechange', this.updateVolumeState.bind(this))
  }

  initVolumeSlider() {
    if (!this.volumeSlider) return

    this.volumeSlider.min = '0'
    this.volumeSlider.max = '100'
    if (this.volumeProgress) this.volumeProgress.max = 100

    if (this.volumeSlider) {
      this.volumeSlider.setAttribute('aria-valuemin', this.volumeSlider.min)
      this.volumeSlider.setAttribute('aria-valuemax', this.volumeSlider.max)
    }

    this.volumeSlider.addEventListener('change', () => {
      const volumeSliderValue = parseInt(this.volumeSlider.value, 10)
      this.setVolume(volumeSliderValue / 100)
    })

    this.volumeSlider.addEventListener('input', () => {
      if (!this.media) return
      const volumeSliderValue = parseInt(this.volumeSlider.value, 10)
      if (this.media.muted) this.setMute(false)
      this.media.volume = this.volumeToFixed(volumeSliderValue / 100)
    })
  }

  volumeToFixed(value: number): number {
    value = parseFloat(value.toFixed(1))
    if (value > 0.9) value = 1
    if (value < 0.1) value = 0
    return value
  }

  setVolume(value: number, triggerInteractionEvent = true, storeValue = true) {
    if (typeof value !== 'number' || !this.media || !this.volumeSlider) return // without volumeSlider element present you shouldn't be able to set volume
    value = this.volumeToFixed(value)
    this.media.volume = value
    if (value > 0) this.volumeMemo = value
    if (value === 0) this.setMute(true, triggerInteractionEvent, storeValue)
    if (triggerInteractionEvent) this.interactionEvent('volume', value)
    if (storeValue && value > 0) this.storeValue('volume', value)
  }

  volumeUp() {
    if (!this.media) return
    let step = this.defaultVolumeStep

    if (this.media.muted) {
      step = 0
      this.setMute(false)
    }

    this.setVolume(this.media.volume + step)
  }

  volumeDown() {
    if (!this.media) return
    this.setVolume(this.media.volume - this.defaultVolumeStep)
  }

  // SKIP
  skipForward() {
    this.skip(false)
    this.interactionEvent('skip-forward')
  }

  skipBackward() {
    this.skip(true)
  }

  skip(backward: boolean, skipStep?: number): void {
    if (!this.media || this.isLive) return
    const mod = backward ? -1 : 1
    skipStep = skipStep || this.defaultSkipStep
    let newTime = this.media.currentTime + skipStep * mod
    if (newTime < 0) newTime = 0
    if (newTime > this.media.duration) newTime = this.media.duration
    this.media.currentTime = newTime

    if (this.media.paused) {
      this.setCurrentTimeLabel(this.media.currentTime)
      this.setDiffTimeLabel(this.media.currentTime, this.media.duration)
      this.setScrubberPosition(this.media.currentTime)
    }
  }

  // BUFFERING PROGRESS
  initBufferingProgress() {
    if (!this.bufferProgress || !this.media) return
    this.bufferProgress.max = 100

    this.media.addEventListener('progress', () => {
      if (this.media.duration <= 0 || !this.media.buffered || !this.media.buffered.length) return
      this.bufferProgress.value = (this.media.buffered.end(0) / this.media.duration) * 100
    })
  }

  // For common interface purposes
  toggleFullscreen() {
    return
  }

  toggleCaptions() {
    return
  }

  // UTILS
  events(element: HTMLElement, events: string, callback: (event: Event) => void): void {
    for (const eventName of events.toLowerCase().split(' ')) {
      element.addEventListener(eventName, (event: Event) => {
        callback(event)
      })
    }
  }

  storeValue(key: string, value: unknown): void {
    localStorage.setItem(`github-media-player-${key}`, JSON.stringify(value))
  }

  retrieveValue(key: string): unknown {
    const value = localStorage.getItem(`github-media-player-${key}`)
    return value ? JSON.parse(value) : null
  }

  isMobile(): boolean {
    if ('maxTouchPoints' in navigator) return navigator.maxTouchPoints > 0

    const mQ = matchMedia?.('(pointer:coarse)')
    if (mQ?.media === '(pointer:coarse)') return !!mQ.matches

    if ('orientation' in window) return true

    return (
      // @ts-expect-error typescript thinks this is never, because maxTouchPoints is always in navigator types
      /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(navigator.userAgent) ||
      // @ts-expect-error typescript thinks this is never, because maxTouchPoints is always in navigator types
      /\b(Android|Windows Phone|iPad|iPod)\b/i.test(navigator.userAgent)
    )
  }

  zeroFormat(value: number): string {
    return value < 10 ? `0${value}` : `${value}`
  }

  formatTime(value: number): string {
    let min = Math.floor(value / 60)
    const sec = Math.floor(value % 60)

    if (min > 59) {
      const hrs = Math.floor(min / 60)
      min = Math.floor(min % 60)
      return `${this.zeroFormat(hrs)}:${this.zeroFormat(min)}:${this.zeroFormat(sec)}`
    }

    return `${this.zeroFormat(min)}:${this.zeroFormat(sec)}`
  }

  calculatePercentagePostion(current: number, max: number): number {
    return (current * 100) / max
  }

  supportsFullscreen(): boolean {
    // eslint-disable-next-line compat/compat
    const fullscreenEnabled = document.fullscreenEnabled
    return !!(
      fullscreenEnabled ||
      // @ts-expect-error browser specific methods are not defined in typescript
      document.webkitFullscreenEnabled ||
      // @ts-expect-error browser specific methods are not defined in typescript
      this.webkitRequestFullScreen ||
      // @ts-expect-error browser specific methods are not defined in typescript
      this.media.webkitEnterFullscreen
    )
  }

  interactionEvent(type: string, value: unknown = null) {
    const detail = {target: this, type, value}
    if (value !== undefined || value !== null) detail.value = value
    document.dispatchEvent(new CustomEvent('media-player-interaction', {detail}))
  }
}
